import { useState, useRef, useEffect } from "react";

interface ConversationMessage {
  text: string;
  timestamp: number;
  type: 'user' | 'vox';
}

interface DuplexAudioProps {
  jobDescription?: string;
  resumeText?: string;
}

export const useDuplexAudio = ({ jobDescription, resumeText }: DuplexAudioProps = {}) => {
  // Refs for audio context and nodes
  const audioContextRef = useRef<AudioContext | null>(null);
  const processorRef = useRef<ScriptProcessorNode | null>(null);
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const websocketRef = useRef<WebSocket | null>(null);
  const nextPlayTimeRef = useRef<number>(0);
  const playingSourcesRef = useRef<AudioBufferSourceNode[]>([]);
  const dataProcessedRef = useRef<{resume: boolean; jobDescription: boolean}>({ resume: false, jobDescription: false });

  // UI States
  const [status, setStatus] = useState<"Connected" | "Disconnected" | "Error">("Disconnected");
  const [isStartButtonDisabled, setStartButtonDisabled] = useState(false);
  const [isStopButtonDisabled, setStopButtonDisabled] = useState(true);
  const [conversationHistory, setConversationHistory] = useState<Record<string, ConversationMessage>>({});
  const [isSystemReady, setIsSystemReady] = useState(false);

  // Audio config
  const SAMPLE_RATE = 16000;
  const CHANNELS = 1;

  // Sample texts
  const sampleResume = `I am a highly skilled and experienced Software Engineer with over 5 years of extensive expertise in full-stack development. My professional journey has equipped me with a strong proficiency in a variety of modern technologies, particularly in using React for front-end development, which allows me to create dynamic and responsive user interfaces. On the server side, I have a solid command of Node.js, enabling me to build robust and scalable applications that can handle high traffic and complex data interactions. Additionally, I possess a strong background in Python, which I utilize for various backend tasks, including data processing and integration with different APIs.

  Throughout my career, I have successfully led multiple high-impact projects that not only met but consistently exceeded client expectations. This has resulted in a significant improvement in system performance, with enhancements of approximately 40% in efficiency and speed. My strong problem-solving skills have been instrumental in identifying and addressing challenges swiftly, ensuring that project timelines are met without compromising quality.

  Collaboration is another key aspect of my work ethic; I thrive in team environments where I can share knowledge and learn from others. I believe that effective communication and teamwork are essential for the success of any project. I hold a Bachelor's degree in Computer Science, which has provided me with a solid foundation in software engineering principles and practices, equipping me with the theoretical knowledge and practical skills necessary to excel in this field. My passion for technology drives me to stay updated with the latest industry trends and continuously improve my skill set.`;

  const sampleJobDescription = `We are actively seeking a highly skilled Senior Software Engineer who possesses strong expertise in React and modern JavaScript frameworks. The ideal candidate must have a proven track record of working with WebSocket implementations and developing real-time applications that significantly enhance user experience. This role requires a deep understanding of asynchronous programming and the ability to manage state effectively in complex applications. Additionally, knowledge of Python for backend development will be considered a significant advantage, as it will allow for seamless integration between front-end and back-end systems.

  The position demands a minimum of 3 years of relevant experience in software development, with a strong emphasis on building scalable and maintainable code. Candidates should demonstrate a passion for technology and a commitment to continuous learning and improvement, as the field is constantly evolving. We value individuals who are proactive in seeking out new challenges and who thrive in collaborative environments.

  Furthermore, the ideal candidate should possess excellent problem-solving skills and the ability to work under pressure while meeting tight deadlines. Strong communication skills are essential, as the role involves working closely with cross-functional teams to deliver high-quality software solutions. If you are a motivated self-starter who is eager to contribute to innovative projects and make a meaningful impact, we encourage you to apply for this exciting opportunity.`;

  useEffect(() => {
    // console.log("Conversation History Updated:", conversationHistory);
  }, [conversationHistory]);

  // Initialize Audio Context
  const initializeAudioContext = async () => {
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)({
        sampleRate: SAMPLE_RATE,
        channelCount: CHANNELS,
      });

      if (audioContextRef.current.state === "suspended") {
        await audioContextRef.current.resume();
      }
      nextPlayTimeRef.current = audioContextRef.current.currentTime;
    }
  };

  // Convert PCM float array to hex-encoded Int16
  const encodePCMToHex = (audioData: Float32Array) => {
    const int16Array = new Int16Array(audioData.length);
    for (let i = 0; i < audioData.length; i++) {
      let s = Math.max(-1, Math.min(1, audioData[i]));
      int16Array[i] = Math.round(s * 32767);
    }

    let hexEncodedAudioChunk = "";
    for (let i = 0; i < int16Array.length; i++) {
      let val = int16Array[i];
      let hex = ((val & 0xff) << 8) | ((val >> 8) & 0xff);
      hexEncodedAudioChunk += hex.toString(16).padStart(4, "0");
    }
    return hexEncodedAudioChunk;
  };

  // Convert a hex string to a Uint8Array
  const hexToUint8Array = (hexString: string) => {
    if (hexString.length % 2 !== 0) {
      throw new Error("Invalid hex string");
    }
    const byteArray = hexString
      .match(/.{1,2}/g)!
      .map((byte) => parseInt(byte, 16));
    return new Uint8Array(byteArray);
  };

  // Play received audio chunk
  const playAudioChunk = (audioData: Uint8Array) => {
    try {
      if (!audioContextRef.current) return;

      const audioBuffer = audioContextRef.current.createBuffer(
        CHANNELS,
        audioData.byteLength / 2,
        SAMPLE_RATE
      );

      const channelData = audioBuffer.getChannelData(0);
      const view = new Int16Array(audioData.buffer);

      for (let i = 0; i < view.length; i++) {
        channelData[i] = view[i] / 32768.0;
      }

      const source = audioContextRef.current.createBufferSource();
      source.buffer = audioBuffer;
      source.connect(audioContextRef.current.destination);

      const currentTime = audioContextRef.current.currentTime;
      const startTime = Math.max(currentTime, nextPlayTimeRef.current);
      source.start(startTime);

      // Store the source in the playingSourcesRef
      playingSourcesRef.current.push(source);

      nextPlayTimeRef.current = startTime + audioBuffer.duration;
    } catch (error) {
      console.error("Error playing audio chunk:", error);
    }
  };

  const connectDuplexWebSocket = async () => {
    try {
      await initializeAudioContext();

      if (websocketRef.current) {
        websocketRef.current.close();
      }

      const userId = crypto.randomUUID();
      websocketRef.current = new WebSocket(`wss://aiinterview.eastus.cloudapp.azure.com/ws/?user_id=${userId}`);
      websocketRef.current.binaryType = "arraybuffer";

      websocketRef.current.onopen = () => {
        console.log("Duplex WebSocket connected");
    setStatus("Connected");
        setIsSystemReady(false);
        
        let hasDataToProcess = false;
        dataProcessedRef.current = {
          resume: !resumeText,
          jobDescription: !jobDescription
        };
        
        // Send resume text and job description if available
        if (websocketRef.current) {
          if (resumeText) {
            console.log("Sending resume text to server...");
            websocketRef.current.send(JSON.stringify({
              type: "user_resume",
              resume_text: resumeText
            }));
            hasDataToProcess = true;
          }

          if (jobDescription) {
            console.log("Sending job description to server...");
            websocketRef.current.send(JSON.stringify({
              type: "user_job_description",
              job_description_text: jobDescription
            }));
            hasDataToProcess = true;
          }

          // If no data to process, set system as ready immediately
          if (!hasDataToProcess) {
            console.log("No resume or job description to process, system ready");
            setIsSystemReady(true);
          } else {
            // Set a timeout to make system ready if no response received
            setTimeout(() => {
              if (!isSystemReady) {
                console.log("Resume processing timeout, setting system as ready");
                setIsSystemReady(true);
              }
            }, 10000); // 10 second timeout
          }
        }

        if (audioContextRef.current) {
          nextPlayTimeRef.current = audioContextRef.current.currentTime;
        }
      };

      websocketRef.current.onmessage = (event) => {
        try {
          if (event.data) {
            const message = JSON.parse(event.data);
            const { type } = message;

            if (type === "system") {
              if (message.message === "resume_processed") {
                console.log("Resume processed successfully");
                dataProcessedRef.current.resume = true;
                if (dataProcessedRef.current.jobDescription) {
                  setIsSystemReady(true);
                }
              } else if (message.message === "job_description_processed") {
                console.log("Job description processed successfully");
                dataProcessedRef.current.jobDescription = true;
                if (dataProcessedRef.current.resume) {
                  setIsSystemReady(true);
                }
              } else if (message.message === "resume_and_job_description_ready") {
                console.log("System is ready for audio streaming");
                setIsSystemReady(true);
              } else if (message.message === "processing_started") {
                console.log("Resume processing started");
                setIsSystemReady(false);
              }
            }
            else if (type === "res_audio") {
              const { audio_chunk } = message;
              if (audio_chunk) {
                const audioData = hexToUint8Array(audio_chunk);
                playAudioChunk(audioData);
              }
            }
            else if (type === "vox_text") {
              const { text, time_stamp } = message;
              setConversationHistory((prev) => ({
                ...prev,
                [time_stamp]: {
                  text,
                  timestamp: parseInt(time_stamp),
                  type: 'vox'
                }
              }));
            }
            else if (type === "user_text") {
              const { text, time_stamp } = message;
              setConversationHistory((prev) => ({
                ...prev,
                [time_stamp]: {
                  text,
                  timestamp: parseInt(time_stamp),
                  type: 'user'
                }
              }));
            }
            else if (type === "stop_vox_audio") {
              console.log("VOX audio stopped");
              // Stop all currently scheduled but unplayed audio sources
              playingSourcesRef.current.forEach(source => {
                try {
                  source.stop();  // Stop playback immediately
                } catch (err) {
                  console.error("Error stopping scheduled audio source:", err);
                }
              });
              // Clear the array of active sources from memory
              playingSourcesRef.current = [];
              if (audioContextRef.current) {
                nextPlayTimeRef.current = audioContextRef.current.currentTime;
              }
            }
          }
        } catch (error) {
          console.error("Error processing WebSocket message:", error);
        }
      };

      websocketRef.current.onclose = (event) => {
        console.log("WebSocket closed:", event.code, event.reason);
        setStatus("Disconnected");
      };

      websocketRef.current.onerror = (error) => {
        console.error("WebSocket error:", error);
        setStatus("Error");
      };
    } catch (error) {
      console.error("Error setting up WebSocket:", error);
      setStatus("Error");
    }
  };

  const startStreaming = async () => {
    if (!isSystemReady) {
      console.warn("System is not ready. Please wait for resume and job description processing.");
      return;
    }

    if (!websocketRef.current || websocketRef.current.readyState !== WebSocket.OPEN) {
      console.warn("WebSocket is not open. Call connectDuplexWebSocket first.");
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
      sourceRef.current = audioContextRef.current.createMediaStreamSource(stream);

      processorRef.current = audioContextRef.current.createScriptProcessor(1024, 1, 1);
      sourceRef.current.connect(processorRef.current);
      processorRef.current.connect(audioContextRef.current.destination);

      processorRef.current.onaudioprocess = (e) => {
        const audioData = e.inputBuffer.getChannelData(0);
        const hexEncodedAudioChunk = encodePCMToHex(audioData);

        if (websocketRef.current && websocketRef.current.readyState === WebSocket.OPEN) {
          websocketRef.current.send(
            JSON.stringify({
              type: "user_audio",
              audio_chunk: hexEncodedAudioChunk,
            })
          );
        }
      };

    setStartButtonDisabled(true);
      setStopButtonDisabled(false);
    } catch (error) {
      console.error("Error starting audio stream:", error);
      setStatus("Error");
    }
  };

  const stopStreaming = () => {
    if (processorRef.current) processorRef.current.disconnect();
    if (sourceRef.current) sourceRef.current.disconnect();
    processorRef.current = null;
    sourceRef.current = null;

    setStartButtonDisabled(false);
    setStopButtonDisabled(true);
  };

  const getSortedConversation = () => {
    const values = Object.values(conversationHistory);
    return values.sort((a, b) => a.timestamp - b.timestamp);
  };

  const cleanupAudioAndConnections = () => {
    if (websocketRef.current) {
      websocketRef.current.close();
      websocketRef.current = null;
    }
    // Clear any playing audio sources
    if (playingSourcesRef.current) {
      playingSourcesRef.current.forEach(source => {
        try {
          source.stop();
        } catch (err) {
          console.error("Error stopping audio source:", err);
        }
      });
      playingSourcesRef.current = [];
    }
    // Reset audio context
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
    if (processorRef.current) {
      processorRef.current.disconnect();
      processorRef.current = null;
    }
    if (sourceRef.current) {
      sourceRef.current.disconnect();
      sourceRef.current = null;
    }
  };

  return {
    status,
    isStartButtonDisabled,
    isStopButtonDisabled,
    isSystemReady,
    conversationHistory,
    connectDuplexWebSocket,
    startStreaming,
    stopStreaming,
    getSortedConversation,
    cleanupAudioAndConnections
  };
};